import type { ChildAccidentMissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import { useRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import { AddressField, CprField, Grid, GridItem, validateCpr } from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import { useState } from 'react';
import { isContainingDuplicateCPR } from '../configs/ValidateMissingDTOConfig';
import { getDawaIdFromkvhxUrl } from '../../../utils/addressUtil';

const ChildAccidentMissingInformationForm = ({
    childAccidentMissingInformation
}: {
    childAccidentMissingInformation: ChildAccidentMissingInformationDTO;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [missingInformation, setMissingInformation] = useRecoilState(missingInformationAtom);
    const { cpr, fixedId, address, isDummyContact } = childAccidentMissingInformation;
    const [cprNr, setCprNr] = useState<string>('');
    const isCprNrValid = cprNr ? validateCpr(cprNr) : undefined;
    const isDuplicate = isContainingDuplicateCPR(missingInformation.childAccident ?? []);

    const updateMissingInformationProperty = (
        updateValue: ChildAccidentMissingInformationDTO
    ): void => {
        setMissingInformation((previousState) => {
            const updateDto = previousState.childAccident?.map(
                (item: ChildAccidentMissingInformationDTO) => {
                    if (item.fixedId !== fixedId) return item;
                    return {
                        ...item,
                        ...updateValue
                    };
                }
            );
            return {
                ...previousState,
                childAccident: updateDto
            };
        });
    };

    const updateCpr = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const formattedCPR = event.target.value.replace(/\D/g, '');
        setCprNr(formattedCPR);
        if (childAccidentMissingInformation.cpr !== formattedCPR) {
            updateMissingInformationProperty({
                cpr: formattedCPR
            });
        }
    };

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        const { address, kvhxUrl } = fieldEventDataProps;
        const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);
        updateMissingInformationProperty({ address, dawaId });
    };

    return (
        <Grid key={childAccidentMissingInformation.fixedId} gap={4}>
            <GridItem span={12}>
                <h6 className="u-spacing-stack-none">{childAccidentMissingInformation.fullName}</h6>
            </GridItem>
            <GridItem span={12}>
                <CprField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-CHILDCPR')}
                    value={isDummyContact ? '' : cpr}
                    onBlur={updateCpr}
                    isValid={isCprNrValid && !isDuplicate}
                    errorText={
                        isDuplicate
                            ? getHippoText('DSS-ADDITIONAL-INFORMATION-DUPLICATE-CPR')
                            : undefined
                    }
                />
            </GridItem>
            <GridItem span={12}>
                <h6 className="u-spacing-stack-none">
                    {getHippoText('DSS-ADDITIONAL-INFORMATION-CHILDADDRESS-HEADER')}
                </h6>
            </GridItem>
            <GridItem span={12}>
                <AddressField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-CHILDADDRESS')}
                    defaultValue={isDummyContact ? '' : address}
                    fieldEvent={(data) => {
                        updateAddress(data);
                    }}
                />
            </GridItem>
        </Grid>
    );
};
export default ChildAccidentMissingInformationForm;
